/*
 * gv-high-variant.js
 * Completes the variant picker for products with more than 250 variants.
 *
 * The theme's <variant-selection> component fetches {product.url}.js, which
 * Shopify caps at 250 variants, so combinations past the cap can never be
 * selected. For pickers marked data-high-variant (see product__form.liquid)
 * this script replaces the component's product fetch: the capped .js payload
 * still supplies the product-level fields, while the full variant list is
 * rebuilt by paging the gv-hv-resolver section through the Section Rendering
 * API (240 variants per page). Every other consumer - option graying,
 * selectCallbackTheme, price UI, history, the shipping ETA widget - keeps
 * working on the standard shapes it already expects.
 *
 * Any fetch failure falls back to the capped .js product, i.e. the previous
 * behavior. Diagnostics land on window.__gvHV.
 */
(function () {
  'use strict';

  var MARK = 'data-high-variant';
  var SECTION_ID = 'gv-hv-resolver';

  function parsePayload(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector('[data-gv-hv-resolved]');
    if (!el) throw new Error('gv-hv: resolver payload missing');
    return JSON.parse(el.textContent);
  }

  function fetchPage(productUrl, page) {
    var sep = productUrl.indexOf('?') === -1 ? '?' : '&';
    var url = productUrl + sep + 'section_id=' + SECTION_ID + '&page=' + page;
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('gv-hv: HTTP ' + r.status + ' fetching ' + url);
      return r.text();
    }).then(parsePayload);
  }

  function fetchAllVariants(productUrl) {
    return fetchPage(productUrl, 1).then(function (first) {
      if (!first || !Array.isArray(first.variants) || !first.pages) {
        throw new Error('gv-hv: malformed resolver payload');
      }
      if (first.pages === 1) {
        return { pages: 1, total: first.total_items, variants: first.variants, product: first.product || null };
      }
      var rest = [];
      for (var p = 2; p <= first.pages; p += 1) rest.push(fetchPage(productUrl, p));
      return Promise.all(rest).then(function (results) {
        var all = first.variants.slice();
        results.forEach(function (r) {
          if (!r || !Array.isArray(r.variants)) throw new Error('gv-hv: malformed page payload');
          all = all.concat(r.variants);
        });
        return { pages: first.pages, total: first.total_items, variants: all, product: first.product || null };
      });
    });
  }

  function patchPrototype(VS) {
    var proto = VS.prototype;
    if (proto._gvHVPatched) return;
    proto._gvHVPatched = true;

    var origFetchProduct = proto._fetchProduct;

    proto._fetchProduct = function () {
      var el = this;
      if (!el.hasAttribute(MARK)) return origFetchProduct.apply(el, arguments);

      var ajaxUrl = el.getAttribute('product-url') || '';
      var pageUrl = ajaxUrl.replace(/\.js(\?.*)?$/, '');
      var basePromise = fetch(ajaxUrl, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('gv-hv: HTTP ' + r.status + ' fetching ' + ajaxUrl);
        return r.json();
      });

      return fetchAllVariants(pageUrl).then(function (full) {
        // Draft products 404 on the Ajax .js endpoint; the resolver's Liquid
        // product blob covers the product-level fields in that case.
        return basePromise.catch(function () { return null; }).then(function (base) {
          var product = base || full.product;
          if (!product) throw new Error('gv-hv: no product data from any source');
          if (!Array.isArray(product.variants) || full.variants.length >= product.variants.length) {
            product.variants = full.variants;
          }
          window.__gvHV = {
            productId: product.id,
            pages: full.pages,
            reportedTotal: full.total,
            variants: product.variants.length,
            base: base ? 'ajax' : 'resolver',
            fallback: false
          };
          el._product = product;
          return product;
        });
      }).catch(function (err) {
        console.warn('[gv-hv] full variant fetch failed; using capped variant data.', err);
        return basePromise.then(function (product) {
          window.__gvHV = { productId: product.id, variants: product.variants.length, fallback: true, error: String(err) };
          el._product = product;
          return product;
        }).catch(function () {
          el._product = null;
          return null;
        });
      }).then(function (product) {
        el._loaded = true;
        return product;
      });
    };
  }

  // Elements upgraded before this script ran (the product page's own picker)
  // already started the capped fetch from their connectedCallback; redo the
  // fetch through the patched path and refresh dependents once it lands.
  function reinit(el) {
    el._loaded = false;
    el._productFetcher = el._fetchProduct();
    el._productFetcher.then(function (product) {
      if (!product) return;
      document.querySelectorAll('options-selection').forEach(function (os) {
        if (os.getVariantSelection && os.getVariantSelection() === el && os._onOptionChange) {
          os._onOptionChange();
        }
      });
      var current = el.getAttribute('variant');
      if (current) el._changeVariant(current);
    });
  }

  function start() {
    var VS = customElements.get('variant-selection');
    if (!VS) return; // resolved via whenDefined below
    patchPrototype(VS);
    document.querySelectorAll('variant-selection[' + MARK + ']').forEach(reinit);
  }

  if (customElements.get('variant-selection')) {
    start();
  } else if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('variant-selection').then(start);
  }
})();
